import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { FlowPeriod, FlowNode } from '../../services/flow-service';
import {
  type Point, type Viewport, toWorld, zoomAt, layoutColumns,
  nodeRadius, edgeWidth, typeColorVar, fitToNodes, bezierPoint,
} from './useFlowGraph';

interface FlowCanvasProps {
  period: FlowPeriod;
  /** Union of nodes across all periods, so layout is stable while scrubbing. */
  allNodes: FlowNode[];
  viewport: Viewport;
  onViewportChange: (v: Viewport) => void;
  positions: Record<string, Point>;
  onPositionsChange: (p: Record<string, Point>) => void;
  hidden: Set<string>;
  colorOverrides: Record<string, string>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  height?: number;
}

interface Particle {
  edge: number;
  t: number;
  speed: number;
}

const cssVar = (name: string, fallback: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

/**
 * The money-flow map.
 *
 * Canvas rather than SVG because a busy month renders several hundred moving
 * particles; one DOM node each would thrash layout every frame.
 *
 * Nodes live in world space and are transformed on draw, which is what lets
 * dragging, zooming and panning all stay correct at any scale.
 */
export const FlowCanvas: React.FC<FlowCanvasProps> = ({
  period, allNodes, viewport, onViewportChange, positions, onPositionsChange,
  hidden, colorOverrides, selectedId, onSelect, height = 560,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef(0);
  const particlesRef = useRef<Particle[]>([]);
  const dragRef = useRef<{ kind: 'node' | 'pan'; id?: string; last: Point } | null>(null);

  const [size, setSize] = useState({ w: 0, h: height });
  const [hovered, setHovered] = useState<string | null>(null);

  // Live refs so the animation loop never restarts on interaction state.
  const viewportRef = useRef(viewport);
  const positionsRef = useRef(positions);
  const hoveredRef = useRef(hovered);
  useEffect(() => { viewportRef.current = viewport; }, [viewport]);
  useEffect(() => { positionsRef.current = positions; }, [positions]);
  useEffect(() => { hoveredRef.current = hovered; }, [hovered]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [height]);

  const visibleNodes = useMemo(
    () => period.nodes.filter((n) => !hidden.has(n.account_id)),
    [period.nodes, hidden],
  );

  const visibleEdges = useMemo(
    () => period.edges.filter((e) => !hidden.has(e.source) && !hidden.has(e.target)),
    [period.edges, hidden],
  );

  const maxVolume = useMemo(
    () => Math.max(1, ...period.nodes.map((n) => Number(n.volume))),
    [period.nodes],
  );
  const maxEdge = useMemo(
    () => Math.max(1, ...visibleEdges.map((e) => Number(e.value))),
    [visibleEdges],
  );

  const nodeById = useMemo(() => {
    const m = new Map<string, FlowNode>();
    for (const n of period.nodes) m.set(n.account_id, n);
    return m;
  }, [period.nodes]);

  const colorFor = useCallback((n: FlowNode): string => {
    const override = colorOverrides[n.account_id];
    if (override) return override;
    return cssVar(typeColorVar(n.account_type), '#2dd4a7');
  }, [colorOverrides]);

  const radiusFor = useCallback(
    (id: string) => nodeRadius(Number(nodeById.get(id)?.volume ?? 0), maxVolume),
    [nodeById, maxVolume],
  );

  // Rebuild particles when the visible edge set changes. Count per edge scales
  // with value, which is what makes a busy edge read as busy.
  useEffect(() => {
    const next: Particle[] = [];
    visibleEdges.forEach((e, i) => {
      const share = Number(e.value) / maxEdge;
      const count = Math.max(1, Math.round(share * 12));
      for (let p = 0; p < count; p++) {
        next.push({ edge: i, t: Math.random(), speed: 0.0018 + share * 0.0032 });
      }
    });
    particlesRef.current = next;
  }, [visibleEdges, maxEdge]);

  // --- render loop ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !size.w) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.w * dpr;
    canvas.height = size.h * dpr;
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const colors = {
      text: cssVar('--text', '#f1f5f9'),
      dim: cssVar('--text-dim', '#9aa4b2'),
    };

    const draw = () => {
      const v = viewportRef.current;
      const pos = positionsRef.current;
      const hov = hoveredRef.current;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size.w, size.h);
      ctx.save();
      // One transform for the whole scene keeps world coords in the maths and
      // out of every individual draw call.
      ctx.translate(v.x, v.y);
      ctx.scale(v.k, v.k);

      const focus = hov ?? selectedId;
      const isDim = (a: string, b: string) => focus && a !== focus && b !== focus;

      // --- edges ---
      for (const e of visibleEdges) {
        const a = pos[e.source], b = pos[e.target];
        if (!a || !b) continue;
        const sn = nodeById.get(e.source), tn = nodeById.get(e.target);
        if (!sn || !tn) continue;

        const grad = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
        grad.addColorStop(0, colorFor(sn));
        grad.addColorStop(1, colorFor(tn));

        ctx.globalAlpha = isDim(e.source, e.target) ? 0.05 : 0.3;
        ctx.strokeStyle = grad;
        ctx.lineWidth = edgeWidth(Number(e.value), maxEdge) / v.k;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        const mx = (a.x + b.x) / 2;
        ctx.bezierCurveTo(mx, a.y, mx, b.y, b.x, b.y);
        ctx.stroke();
      }

      // --- particles ---
      for (const p of particlesRef.current) {
        const e = visibleEdges[p.edge];
        if (!e) continue;
        if (isDim(e.source, e.target)) continue;
        const a = pos[e.source], b = pos[e.target];
        const tn = nodeById.get(e.target);
        if (!a || !b || !tn) continue;

        const pt = bezierPoint(a, b, p.t);
        ctx.globalAlpha = 0.95;
        ctx.fillStyle = colorFor(tn);
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 2.1 / v.k, 0, Math.PI * 2);
        ctx.fill();

        if (!reduceMotion) {
          p.t += p.speed;
          if (p.t > 1) p.t -= 1;
        }
      }

      // --- nodes ---
      for (const n of visibleNodes) {
        const p = pos[n.account_id];
        if (!p) continue;
        const r = radiusFor(n.account_id);
        const c = colorFor(n);
        const isFocus = focus === n.account_id;
        const dim = focus && !isFocus;

        if (isFocus) {
          ctx.globalAlpha = 0.18;
          ctx.fillStyle = c;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r + 9 / v.k, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.globalAlpha = dim ? 0.12 : 0.24;
        ctx.fillStyle = c;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = dim ? 0.25 : 1;
        ctx.strokeStyle = c;
        ctx.lineWidth = (selectedId === n.account_id ? 2.6 : 1.6) / v.k;
        ctx.stroke();

        // Labels are drawn at constant screen size: scaling them with zoom
        // makes them illegible when zoomed out and enormous when zoomed in.
        ctx.globalAlpha = dim ? 0.3 : 1;
        ctx.fillStyle = isFocus ? colors.text : colors.dim;
        const fontPx = 11 / v.k;
        ctx.font = `${isFocus ? 600 : 500} ${fontPx}px Inter, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const label = n.name.length > 22 ? `${n.name.slice(0, 21)}…` : n.name;
        ctx.fillText(label, p.x, p.y + r + 5 / v.k);
      }

      ctx.restore();
      ctx.globalAlpha = 1;
      frameRef.current = requestAnimationFrame(draw);
    };

    frameRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frameRef.current);
  }, [size, visibleEdges, visibleNodes, nodeById, maxEdge, colorFor, radiusFor, selectedId]);

  // --- hit testing ---
  const nodeAt = useCallback((screen: Point): string | null => {
    const world = toWorld(screen, viewportRef.current);
    // Reverse order so the topmost drawn node wins an overlap.
    for (let i = visibleNodes.length - 1; i >= 0; i--) {
      const n = visibleNodes[i];
      const p = positionsRef.current[n.account_id];
      if (!p) continue;
      const r = radiusFor(n.account_id);
      if ((world.x - p.x) ** 2 + (world.y - p.y) ** 2 <= r * r) return n.account_id;
    }
    return null;
  }, [visibleNodes, radiusFor]);

  const relative = (ev: React.MouseEvent | WheelEvent, el: HTMLElement): Point => {
    const rect = el.getBoundingClientRect();
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
  };

  const handleDown = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const p = relative(ev, ev.currentTarget);
    const id = nodeAt(p);
    dragRef.current = id ? { kind: 'node', id, last: p } : { kind: 'pan', last: p };
  };

  const handleMove = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const p = relative(ev, ev.currentTarget);
    const drag = dragRef.current;

    if (!drag) {
      const id = nodeAt(p);
      if (id !== hoveredRef.current) setHovered(id);
      return;
    }

    const dx = p.x - drag.last.x;
    const dy = p.y - drag.last.y;
    drag.last = p;

    if (drag.kind === 'pan') {
      const v = viewportRef.current;
      onViewportChange({ ...v, x: v.x + dx, y: v.y + dy });
    } else if (drag.id) {
      // Divide by zoom so the node tracks the cursor exactly at any scale.
      const v = viewportRef.current;
      const cur = positionsRef.current[drag.id];
      if (cur) {
        onPositionsChange({
          ...positionsRef.current,
          [drag.id]: { x: cur.x + dx / v.k, y: cur.y + dy / v.k },
        });
      }
    }
  };

  const handleUp = (ev: React.MouseEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    // A click is a press that did not travel: select, don't treat as a drag.
    const p = relative(ev, ev.currentTarget);
    const moved = Math.hypot(p.x - drag.last.x, p.y - drag.last.y) > 3;
    if (!moved && drag.kind === 'node' && drag.id) onSelect(drag.id);
    else if (!moved && drag.kind === 'pan') onSelect(null);
  };

  // Non-passive wheel listener: React's synthetic onWheel is passive, so
  // preventDefault there would not stop the page scrolling.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const rect = el.getBoundingClientRect();
      const p = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
      onViewportChange(zoomAt(viewportRef.current, p, ev.deltaY < 0 ? 1.12 : 1 / 1.12));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onViewportChange]);

  // Initial layout + fit, once the canvas has a size.
  useEffect(() => {
    if (!size.w || Object.keys(positions).length) return;
    const laid = layoutColumns(allNodes, { width: size.w, height: size.h });
    onPositionsChange(laid);
    onViewportChange(fitToNodes(laid, size.w, size.h));
    // Runs only while positions is empty, so it cannot fight user drags.
  }, [size, allNodes, positions, onPositionsChange, onViewportChange]);

  return (
    <Box ref={wrapRef} sx={{ width: '100%', height, position: 'relative' }}>
      <canvas
        ref={canvasRef}
        onMouseDown={handleDown}
        onMouseMove={handleMove}
        onMouseUp={handleUp}
        onMouseLeave={() => { dragRef.current = null; setHovered(null); }}
        style={{
          display: 'block',
          cursor: hovered ? 'grab' : dragRef.current?.kind === 'pan' ? 'grabbing' : 'default',
          touchAction: 'none',
        }}
      />
    </Box>
  );
};
